import React, { useEffect, useMemo, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Button,
  Flex,
  Group,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  IconArrowLeft,
  IconEdit,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';
import { Loading } from '../../../../global/component/organism/Loading/Loading.tsx';
import {
  useAlertRules,
  useAlertRuleConfigurations,
} from '../../../../global/entities/Alert/queries';
import { IAlertRuleConfiguration } from '../../../../global/entities/Alert/Alert.ts';
import {
  useDeleteAlertRuleConfiguration,
  useUpdateAlertRuleConfiguration,
} from '../../../../global/entities/Alert/mutations';
import { useGetHealthcareFacilities } from '../../../../global/entities/healthcareFacility/queries/useGetHealthcareFacilities';
import { useJournals } from '../../../../global/entities/Journal/queries/useJournals.ts';
import { useGetQuestionnaires } from '../../../../global/entities/Questionnaires/queries/useGetQuestionnaires.tsx';
import { useGetPhysiologicalTypes } from '../../../../global/entities/PhysiologicalData/queries/useGetPhysiologicalTypes.tsx';
import AlertConfigurationModal from './AlertConfigurationModal.tsx';
import { severityColor } from './alertConfigurationConstants.ts';
import {
  TableContextProvider,
  Th,
  useTable,
} from '../../../../global/hooks/useTable.tsx';

interface FlatAlertConfig {
  id: number;
  facility: string;
  source: string;
  condition: string;
  severity: string;
  isEnabled: boolean;
  _original: IAlertRuleConfiguration;
}

const flattenConfig = (
  config: IAlertRuleConfiguration,
  facilities: { id: number; name: string }[],
  sourceEntities: { value: string; label: string }[],
  isEn: boolean,
  t: (key: string, opts?: Record<string, unknown>) => string,
): FlatAlertConfig => {
  const facility = facilities.find((f) => f.id === config.healthcareFacilityID);
  const source = sourceEntities.find(
    (s) => s.value === config.sourceEntityID?.toString(),
  );

  let condition = '';
  try {
    const parsed = JSON.parse(config.conditionJson);

    if (parsed.evaluationMode === 'trend' && parsed.trendType) {
      const trendLabel = t(`alertRule.trend.${parsed.trendType.toLowerCase()}`);
      const mode = parsed.valueMode === 'percentage' ? '%' : '';
      condition = `${trendLabel} ${parsed.value}${mode} / ${parsed.sampleCount} ${t('alertRule.trend.samples')}`;
    } else {
      const qLabel =
        (isEn ? parsed.questionTextEn : parsed.questionTextFr) ||
        parsed.questionTextEn ||
        (parsed.journalQuestionId ? `Q#${parsed.journalQuestionId}` : '');
      const category = isEn
        ? parsed.categoryEn
        : parsed.categoryFr || parsed.categoryEn;

      if (category && parsed.operator && parsed.threshold !== undefined)
        condition = `${category} ${parsed.operator} ${parsed.threshold}`;
      else if (
        parsed.journalQuestionId &&
        parsed.operator &&
        parsed.numericThreshold !== undefined
      )
        condition = `${qLabel} ${parsed.operator} ${parsed.numericThreshold}`;
      else if (parsed.journalQuestionId && parsed.triggerValues?.length > 0)
        condition = `${qLabel}: ${parsed.triggerValues.join(', ')}`;
      else if (parsed.journalQuestionId && parsed.keywords?.length > 0)
        condition = `${qLabel}: ${parsed.keywords.join(', ')}`;
      else if (
        parsed.physiologicalTypeId &&
        parsed.operator &&
        parsed.threshold !== undefined
      )
        condition = `${parsed.operator} ${parsed.threshold}${parsed.unitName ? ' ' + parsed.unitName : ''}`;
      else if (parsed.thresholdDays !== undefined)
        {condition = t('alertRule.thresholdDays', {
          count: parsed.thresholdDays,
        });}
      else condition = config.conditionJson;
    }
  } catch {
    condition = config.conditionJson;
  }

  return {
    id: config.id,
    facility: facility?.name || '',
    source: source?.label || '',
    condition,
    severity: config.severity,
    isEnabled: config.isEnabled,
    _original: config,
  };
};

const AlertRuleFormContent: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const ruleId = id ? Number(id) : 0;
  const isEn = i18n.language === 'en';

  const { data: rules, isLoading: isLoadingRules } = useAlertRules();
  const { data: configurations, isLoading: isLoadingConfigs } =
    useAlertRuleConfigurations(ruleId);

  const { data: facilities } = useGetHealthcareFacilities();
  const { data: journals } = useJournals();
  const { data: questionnaires } = useGetQuestionnaires();
  const { data: physiologicalTypes } = useGetPhysiologicalTypes();

  const [opened, { open, close }] = useDisclosure(false);
  const [editingConfig, setEditingConfig] =
    useState<IAlertRuleConfiguration | null>(null);

  const [facilityFilter, setFacilityFilter] = useState<string | null>(null);
  const [sourceFilter, setSourceFilter] = useState<string | null>(null);
  const [severityFilter, setSeverityFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  const deleteConfigMutation = useDeleteAlertRuleConfiguration();
  const updateConfigMutation = useUpdateAlertRuleConfiguration();

  const handleToggleEnabled = (flat: FlatAlertConfig) => {
    const config = flat._original;
    updateConfigMutation.mutate({
      configId: config.id,
      request: {
        conditionJson: config.conditionJson,
        severity: config.severity,
        deliveryChannels: config.deliveryChannels || 'InApp',
        escalationEnabled: config.escalationEnabled,
        escalationDelayMinutes: config.escalationDelayMinutes,
        escalationTargetType: config.escalationTargetType,
        escalationTargetID: config.escalationTargetID,
        level2EscalationEnabled: config.level2EscalationEnabled,
        level2EscalationDelayMinutes: config.level2EscalationDelayMinutes,
        level2EscalationTargetType: config.level2EscalationTargetType,
        level2EscalationTargetID: config.level2EscalationTargetID,
        healthcareFacilityID: config.healthcareFacilityID,
        isEnabled: !config.isEnabled,
      },
    });
  };

  const {
    sortBy,
    reverseSortDirection,
    setSorting,
    setSortedData,
    sortedData,
  } = useTable();

  const rule = rules?.find((r) => r.id === ruleId);

  const sourceEntityOptions = useMemo(() => {
    if (!rule) return [];
    switch (rule.sourceType) {
      case 'Journal':
        return (journals || []).map((j) => ({
          value: j.id.toString(),
          label: j.name,
        }));
      case 'Questionnaire':
        return (questionnaires || []).map((q) => ({
          value: q.id.toString(),
          label: q.name,
        }));
      case 'Physiological': {
        const seenIds = new Set<number>();
        return (physiologicalTypes || [])
          .filter((p) => {
            if (seenIds.has(p.id)) return false;
            seenIds.add(p.id);
            return true;
          })
          .map((p) => ({ value: p.id.toString(), label: p.name }));
      }
      default:
        return [];
    }
  }, [rule, journals, questionnaires, physiologicalTypes]);

  const flatConfigs = useMemo(() => {
    if (!configurations) return [];
    return configurations.map((config) =>
      flattenConfig(config, facilities || [], sourceEntityOptions, isEn, t),
    );
  }, [configurations, facilities, sourceEntityOptions, isEn, t]);

  useEffect(() => {
    let filtered = flatConfigs;
    if (facilityFilter)
      filtered = filtered.filter((c) => c.facility === facilityFilter);
    if (sourceFilter)
      filtered = filtered.filter((c) => c.source === sourceFilter);
    if (severityFilter)
      filtered = filtered.filter((c) => c.severity === severityFilter);
    if (statusFilter)
      {filtered = filtered.filter(
        (c) => (statusFilter === 'enabled') === c.isEnabled,
      );}
    setSortedData(filtered);
  }, [flatConfigs, facilityFilter, sourceFilter, severityFilter, statusFilter]);

  const handleOpenCreate = () => {
    setEditingConfig(null);
    open();
  };

  const handleOpenEdit = (config: IAlertRuleConfiguration) => {
    setEditingConfig(config);
    open();
  };

  const handleDelete = (configId: number) => {
    modals.openConfirmModal({
      title: t('modal.deleteItemModal.title'),
      children: (
        <Stack gap="sm">
          <Text>{t('modal.deleteItemModal.question')}</Text>
          <Text fw="bold">{t('modal.deleteItemModal.details')}</Text>
        </Stack>
      ),
      labels: { confirm: t('button.delete'), cancel: t('button.cancel') },
      confirmProps: { color: 'red' },
      cancelProps: { variant: 'outline', color: 'gray' },
      onConfirm: () => deleteConfigMutation.mutate(configId),
      size: 'lg',
      radius: 'xs',
    });
  };

  const facilityOptions = useMemo(() => {
    const unique = [
      ...new Set(flatConfigs.map((c) => c.facility).filter(Boolean)),
    ];
    return unique.map((f) => ({ value: f, label: f }));
  }, [flatConfigs]);

  const sourceOptions = useMemo(() => {
    const unique = [
      ...new Set(flatConfigs.map((c) => c.source).filter(Boolean)),
    ];
    return unique.map((s) => ({ value: s, label: s }));
  }, [flatConfigs]);

  const severityOptions = useMemo(() => {
    const unique = [
      ...new Set(flatConfigs.map((c) => c.severity).filter(Boolean)),
    ];
    return unique.map((s) => ({ value: s, label: s }));
  }, [flatConfigs]);

  const statusOptions = useMemo(
    () => [
      { value: 'enabled', label: t('alertRule.enabled') },
      { value: 'disabled', label: t('alertRule.disabled') },
    ],
    [t],
  );

  if (isLoadingRules || isLoadingConfigs)
    return <Loading visible={true} loadingMessage={t('global.loading')} />;

  if (!rule) {
    return (
      <Stack mt="xl">
        <Text>{t('alertRule.notFound')}</Text>
        <Button onClick={() => navigate('/adminDashboard/alert-rules')}>
          {t('global.back')}
        </Button>
      </Stack>
    );
  }

  const rows = sortedData as FlatAlertConfig[];

  return (
    <>
      <Flex mb="md" mt="xl" align="center">
        <ActionIcon
          variant="transparent"
          onClick={() => navigate('/adminDashboard/alert-rules')}
          mr="sm"
          aria-label={t('global.back')}
        >
          <IconArrowLeft />
        </ActionIcon>
        <Title order={4}>{isEn ? rule.nameEn : rule.nameFr}</Title>
      </Flex>

      {/* Rule Details */}
      <Paper p="md" mb="lg" withBorder>
        <Stack gap="xs">
          <Group>
            <Text fw={600}>{t('alertRule.detail.code')}:</Text>
            <Text>{rule.code}</Text>
          </Group>
          <Group>
            <Text fw={600}>{t('alertRule.detail.description')}:</Text>
            <Text>{isEn ? rule.descriptionEn : rule.descriptionFr}</Text>
          </Group>
          <Group>
            <Text fw={600}>{t('alertRule.table.sourceType')}:</Text>
            <Text>{rule.sourceType}</Text>
          </Group>
          <Group>
            <Text fw={600}>{t('alertRule.table.evaluationMode')}:</Text>
            <Text>{rule.evaluationMode}</Text>
          </Group>
          <Group>
            <Text fw={600}>{t('alertRule.table.status')}:</Text>
            <Badge color={rule.isEnabled ? 'green' : 'gray'} variant="light">
              {rule.isEnabled
                ? t('alertRule.enabled')
                : t('alertRule.disabled')}
            </Badge>
          </Group>
        </Stack>
      </Paper>

      {/* Configurations Section */}
      <Flex mb="md" align="center" justify="space-between">
        <Title order={5}>{t('alertRule.configurations')}</Title>
        <Button
          leftSection={<IconPlus size={16} />}
          size="sm"
          onClick={handleOpenCreate}
        >
          {t('alertRule.addConfiguration')}
        </Button>
      </Flex>

      <Paper withBorder>
        <Flex p="sm" gap="sm" wrap="wrap">
          <Select
            placeholder={t('alertRule.configTable.facility')}
            data={facilityOptions}
            value={facilityFilter}
            onChange={setFacilityFilter}
            clearable
            searchable
            size="xs"
            comboboxProps={{ width: 'target' }}
            styles={{ root: { flex: 'none' } }}
          />
          <Select
            placeholder={t('alertRule.configTable.source')}
            data={sourceOptions}
            value={sourceFilter}
            onChange={setSourceFilter}
            clearable
            searchable
            size="xs"
            comboboxProps={{ width: 'target' }}
            styles={{ root: { flex: 'none' } }}
          />
          <Select
            placeholder={t('alertRule.configTable.severity')}
            data={severityOptions}
            value={severityFilter}
            onChange={setSeverityFilter}
            clearable
            size="xs"
            comboboxProps={{ width: 'target' }}
            styles={{ root: { flex: 'none' } }}
          />
          <Select
            placeholder={t('alertRule.table.status')}
            data={statusOptions}
            value={statusFilter}
            onChange={setStatusFilter}
            clearable
            size="xs"
            comboboxProps={{ width: 'target' }}
            styles={{ root: { flex: 'none' } }}
          />
        </Flex>
        <ScrollArea w="100%">
          <Table verticalSpacing="xs">
            <Table.Thead>
              {rows && (
                <Table.Tr>
                  <Th
                    sorted={sortBy === 'facility'}
                    reversed={reverseSortDirection}
                    onSort={() => setSorting('facility', rows)}
                  >
                    {t('alertRule.configTable.facility')}
                  </Th>
                  <Th
                    sorted={sortBy === 'source'}
                    reversed={reverseSortDirection}
                    onSort={() => setSorting('source', rows)}
                  >
                    {t('alertRule.configTable.source')}
                  </Th>
                  <Th
                    sorted={sortBy === 'condition'}
                    reversed={reverseSortDirection}
                    onSort={() => setSorting('condition', rows)}
                  >
                    {t('alertRule.configTable.condition')}
                  </Th>
                  <Th
                    sorted={sortBy === 'severity'}
                    reversed={reverseSortDirection}
                    onSort={() => setSorting('severity', rows)}
                  >
                    {t('alertRule.configTable.severity')}
                  </Th>
                  <Th
                    sorted={sortBy === 'isEnabled'}
                    reversed={reverseSortDirection}
                    onSort={() => setSorting('isEnabled', rows)}
                  >
                    {t('alertRule.table.status')}
                  </Th>
                  <Table.Th />
                </Table.Tr>
              )}
            </Table.Thead>
            <Table.Tbody>
              {rows && rows.length > 0 ? (
                rows.map((flat, index) => (
                  <Table.Tr
                    key={flat.id}
                    style={{
                      background:
                        index % 2 === 1
                          ? undefined
                          : 'var(--Background-Gray-light, #E9ECEF)',
                    }}
                  >
                    <Table.Td>
                      <Text size="sm">{flat.facility || '-'}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{flat.source || '-'}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{flat.condition}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        color={severityColor(flat.severity)}
                        variant="light"
                      >
                        {flat.severity}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Switch
                        checked={flat.isEnabled}
                        onChange={() => handleToggleEnabled(flat)}
                        size="sm"
                        color="green"
                      />
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs" wrap="nowrap">
                        <ActionIcon
                          variant="transparent"
                          onClick={() => handleOpenEdit(flat._original)}
                          aria-label={t('global.edit')}
                        >
                          <IconEdit
                            size={18}
                            color={'var(--mantine-color-blue-6)'}
                          />
                        </ActionIcon>
                        <ActionIcon
                          variant="transparent"
                          onClick={() => handleDelete(flat.id)}
                          aria-label={t('button.delete')}
                        >
                          <IconTrash
                            size={18}
                            color={'var(--mantine-color-red-6)'}
                          />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))
              ) : (
                <Table.Tr>
                  <Table.Td colSpan={6}>
                    <Text ta="center" c="dimmed" py="md">
                      {t('alertRule.noConfigurations')}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      </Paper>

      <AlertConfigurationModal
        opened={opened}
        onClose={close}
        rule={rule}
        editingConfig={editingConfig}
        ruleId={ruleId}
        facilities={facilities || []}
        journals={journals || []}
        questionnaires={questionnaires || []}
        physiologicalTypes={physiologicalTypes || []}
      />
    </>
  );
};

const AlertRuleForm: React.FC = () => (
  <TableContextProvider>
    <AlertRuleFormContent />
  </TableContextProvider>
);

export default AlertRuleForm;
